import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { LoginForm } from '../login/LoginForm';
import { transcriptApi } from '../../api/transcriptApi';
import Notification from '../../components/ui/Notification';
import { ClockIcon, ArrowTopRightOnSquareIcon, PlayIcon, PencilSquareIcon, TrashIcon, ArrowPathIcon, ArrowDownTrayIcon, SparklesIcon, DocumentTextIcon, ChevronDownIcon, ChevronUpIcon, Cog6ToothIcon, CheckCircleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import TranscriptStatusDisplay from '../../components/reusable/TranscriptStatusDisplay';
import ReportStatusDisplay from '../../components/reusable/ReportStatusDisplay';
import TranscriptDisplay from '../../components/reusable/TranscriptDisplay';
import ReadMoreText from '../../components/reusable/ReadMoreText';
import { TRANSCRIPT_PROVIDERS, DEFAULT_PROVIDER } from '../../constants/transcriptProviders';
import { formatDuration } from '../../utils/formatDuration';
import { reportApi } from '../../api/reportApi';
import ReportConfigModal from '../../components/reusable/ReportConfigModal';
import PublishModal from '../../components/reusable/PublishModal';
import { getDefaultModel } from '../../config/reportPrompts';

export default function SessionDetailNewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading, error, getAuth } = useAuth();
  const [data, setData] = useState(null);
  const [notif, setNotif] = useState(null);
  const [mdSessionAtGlance, setMdSessionAtGlance] = useState('');
  const [mdFullReport, setMdFullReport] = useState('');
  const [mdTranscript, setMdTranscript] = useState(''); // report tab: Session transcript (summary)
  const [mdSpeakers, setMdSpeakers] = useState('');
  const [mdInDepth, setMdInDepth] = useState('');
  const [knowledgeGraphUrl, setKnowledgeGraphUrl] = useState('');
  const [activeTab, setActiveTab] = useState('report'); // 'report' | 'speakers' | 'graph' | 'analysis'
  const [autoRefresh, setAutoRefresh] = useState(true); // Auto-refresh toggle for transcript processing
  const [selectedProvider, setSelectedProvider] = useState(DEFAULT_PROVIDER);
  const [isGenerating, setIsGenerating] = useState(false);
  const [transcriptSegments, setTranscriptSegments] = useState([]);
  const [loadingTranscript, setLoadingTranscript] = useState(false);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [reportStatus, setReportStatus] = useState(null);
  const [isLoadingReportStatus, setIsLoadingReportStatus] = useState(false);
  const [expandedSpeakers, setExpandedSpeakers] = useState(new Set());
  const [expandedChunks, setExpandedChunks] = useState(new Set());
  const [autoRefreshReport, setAutoRefreshReport] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [showReportConfigModal, setShowReportConfigModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishDestination, setPublishDestination] = useState(null); // 'faicon' | 'wordpress'
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  // Media URL for "View" button.
  // Important: Some API endpoints return Content-Disposition: attachment (forces download).
  // For "View" we prefer direct/public file URLs (like `file_url` / `video_url`) that browsers can stream inline.
  const fullMediaUrl = useMemo(() => {
    if (!data) return null;

    const apiBase = process.env.REACT_APP_API_BASE_URL;

    // Prefer URLs that are meant to be opened in browser (same as list links)
    // Note: `normalizeData` already maps `file_url`, but keep camelCase fallbacks too.
    let fileUrl = data.file_url || data.fileUrl || data.video_url || data.videoUrl;

    // If backend provides the session-video endpoint as file_url, ignore it for "View"
    // because it typically forces download.
    const isSessionVideoEndpoint =
      typeof fileUrl === 'string' && /\/api\/reports\/sessions\/[^/]+\/video\b/.test(fileUrl);
    if (isSessionVideoEndpoint) {
      fileUrl = null;
    }

    // If we don't have a direct URL, use the same fallback as sessions list cards:
    // original_file_id -> /api/files/:id/content
    if (!fileUrl && data.original_file_id) {
      fileUrl = `/api/files/${data.original_file_id}/content`;
    }

    // Intentionally DO NOT fall back to `/api/reports/sessions/:id/video` for View,
    // because that endpoint often forces download via Content-Disposition: attachment.

    // Construct full URL if relative
    const fullUrl = fileUrl && !fileUrl.startsWith('http') ? `${apiBase}${fileUrl}` : fileUrl;
    return fullUrl || null;
  }, [data, id]);

  const handleViewMedia = useCallback(() => {
    if (!fullMediaUrl) return;
    // Open exactly like sessions list does (anchor in a new tab)
    const a = document.createElement('a');
    a.href = fullMediaUrl;
    a.target = '_blank';
    a.rel = 'noreferrer';
    a.click();
  }, [fullMediaUrl]);

  // Normalize backend camelCase to snake_case
  const normalizeData = (session) => ({
    ...session,
    id: session.id,
    user_id: session.userId || session.user_id,
    user_name: session.userName || session.user_name,
    user_email: session.userEmail || session.user_email,
    title: session.title,
    description: session.description,
    status: session.status,
    original_file_id: session.originalFileId || session.original_file_id,
    original_filename: session.originalFilename || session.original_filename,
    storage_path: session.storagePath || session.storage_path,
    processing_started_at: session.processingStartedAt || session.processing_started_at,
    processing_completed_at: session.processingCompletedAt || session.processing_completed_at,
    error_message: session.errorMessage || session.error_message,
    event_id: session.eventId || session.event_id,
    event_title: session.eventTitle || session.event_title,
    event_wp_id: session.eventWpId || session.event_wp_id,
    event_faicon_id: session.eventFaiconId || session.event_faicon_id,
    wp_session_id: session.wpSessionId || session.wp_session_id,
    faicon_session_id: session.faiconSessionId || session.faicon_session_id,
    url: session.url,
    created_at: session.createdAt || session.created_at,
    updated_at: session.updatedAt || session.updated_at,
    has_transcripts: session.hasTranscripts || session.has_transcripts || false,
    has_reports: session.hasReports || session.has_reports || false,
    transcript_count: session.transcriptCount || session.transcript_count || 0,
    report_count: session.reportCount || session.report_count || 0,
    transcript_model: session.transcriptModel || session.transcript_model,
    file_url: session.fileUrl || session.file_url,
    duration_seconds: session.durationSeconds || session.duration_seconds,
    // Transcript-specific fields
    transcript_status: session.transcriptStatus || session.transcript_status,
    transcript_processing_started_at: session.transcriptProcessingStartedAt || session.transcript_processing_started_at,
    transcript_processing_completed_at: session.transcriptProcessingCompletedAt || session.transcript_processing_completed_at,
    transcript_processing_duration: session.transcriptProcessingDuration || session.transcript_processing_duration,
    transcript_error_message: session.transcriptErrorMessage || session.transcript_error_message,
    transcript_provider: session.transcriptProvider || session.transcript_provider,
    // Chunking progress fields
    transcript_chunks_total: session.transcriptChunksTotal || session.transcript_chunks_total,
    transcript_chunks_completed: session.transcriptChunksCompleted || session.transcript_chunks_completed,
    transcript_current_chunk: session.transcriptCurrentChunk || session.transcript_current_chunk,
  });

  // Load transcript segments
  const loadTranscriptData = useCallback(async () => {
    console.log('[loadTranscriptData] Called with data:', {
      hasData: !!data,
      transcript_status: data?.transcript_status,
      id
    });
    
    // Load transcript if status is PROCESSING or COMPLETED (progressive delivery)
    if (!data || (data.transcript_status !== 'COMPLETED' && data.transcript_status !== 'PROCESSING')) {
      console.log('[loadTranscriptData] Skipping - status not COMPLETED or PROCESSING:', data?.transcript_status);
      setTranscriptSegments([]);
      return;
    }

    try {
      console.log('[loadTranscriptData] Loading transcript for session:', id);
      setLoadingTranscript(true);
      const transcriptData = await transcriptApi.getTranscript(id);
      
      console.log('[loadTranscriptData] Received transcript data:', {
        hasSegments: !!transcriptData.segments,
        segmentCount: transcriptData.segments?.length,
        keys: Object.keys(transcriptData)
      });
      
      // Handle different response formats
      let segments = [];
      if (transcriptData.segments) {
        segments = transcriptData.segments;
      } else if (transcriptData.transcript?.segments) {
        segments = transcriptData.transcript.segments;
      } else if (Array.isArray(transcriptData)) {
        segments = transcriptData;
      }
      
      console.log('[loadTranscriptData] Setting segments:', segments.length);
      setTranscriptSegments(segments);
    } catch (e) {
      console.error('[loadTranscriptData] Failed to load transcript:', e);
      // Don't show error notification - transcript might just not exist yet
      setTranscriptSegments([]);
    } finally {
      setLoadingTranscript(false);
    }
  }, [id, data?.transcript_status]);

  // Load session data
  const loadSessionData = useCallback(async () => {
    try {
      const res = await transcriptApi.getSessionById(id);
      if (!res || Object.keys(res).length === 0) {
        setData({
          id,
          title: `Mock Session ${id}`,
          description: 'Mock data for new session page.',
          status: 'IN_PROGRESS',
          created_at: new Date().toISOString(),
          duration_seconds: 1234,
          original_filename: 'mock_audio.mp3',
          file_url: '',
          transcript_model: 'whisper-large-v3',
          processing_started_at: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
          processing_completed_at: new Date().toISOString(),
          report: {
            summary: 'Mock summary for new session page.',
            highlights: ['Point A', 'Point B', 'Point C'],
          }
        });
        setMdSessionAtGlance('## Session at a glance\n- Topic: AI & Governance\n- Speakers: Alice, Bob\n- Key takeaway: Collaboration matters.');
        setMdFullReport('# Full session report\nDetailed narrative here...');
        setMdTranscript('### Transcript (report excerpt)\n`00:00` Alice: Welcome everyone...');
        setMdSpeakers('### Speakers\n- **Alice** — Moderator\n- **Bob** — Panelist');
        setMdInDepth('### In-depth analysis\n- Trend 1\n- Risk 2\n- Opportunity 3');
      } else {
        const normalized = normalizeData(res);
        setData(normalized);
        
        // ALWAYS use markdown fields from session object (source of truth)
        // Even if empty, these take priority over report file data
        // Check both snake_case and camelCase field names
        const sessionAtGlance = res.session_at_glance_md ?? res.sessionAtGlanceMd ?? '';
        const fullReport = res.full_report_md ?? res.fullReportMd ?? '';
        const transcript = res.transcript_md ?? res.transcriptMd ?? '';
        const speakers = res.speakers_md ?? res.speakersMd ?? '';
        const inDepth = res.in_depth_md ?? res.inDepthMd ?? '';
        
        // Set markdown fields immediately - these are the source of truth
        setMdSessionAtGlance(sessionAtGlance);
        setMdFullReport(fullReport);
        setMdTranscript(transcript);
        setMdSpeakers(speakers);
        setMdInDepth(inDepth);
        setKnowledgeGraphUrl(res.knowledge_graph_url || res.knowledgeGraphUrl || knowledgeGraphUrl);
        
        console.log('[loadSessionData] Loaded markdown fields from session:', {
          sessionAtGlance: sessionAtGlance ? `length: ${sessionAtGlance.length}` : 'empty',
          fullReport: fullReport ? `length: ${fullReport.length}` : 'empty',
          speakers: speakers ? `length: ${speakers.length}` : 'empty',
          inDepth: inDepth ? `length: ${inDepth.length}` : 'empty',
        });
      }
    } catch (e) {
      setNotif({ type: 'error', title: 'Failed to load session', message: e.message });
      setData({ id, title: `Mock Session ${id}`, status: 'FAILED', error_message: e.message });
    }
  }, [id, knowledgeGraphUrl]);

  // Initial load
  useEffect(() => {
    if (!user) return;
    // Load session data first, then report status
    // This ensures markdown fields from session are loaded before report status tries to populate them
    loadSessionData().then(() => {
      // Small delay to ensure session data state is updated
      setTimeout(() => {
        loadReportStatus();
      }, 100);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user]); // Removed loadSessionData from deps to prevent infinite loop

  // Load transcript when session data changes and transcript is completed
  useEffect(() => {
    if (!user || !data) return;
    loadTranscriptData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, data?.id, data?.transcript_status]); // Use specific data fields instead of entire data object

  // Track previous chunks_completed to detect changes
  const prevChunksCompleted = useRef(null);

  // Optimized polling: use lightweight chunk progress endpoint
  useEffect(() => {
    if (!autoRefresh || !data || data.transcript_status !== 'PROCESSING') {
      prevChunksCompleted.current = null;
      return;
    }
    
    // Initialize previous value
    if (prevChunksCompleted.current === null) {
      prevChunksCompleted.current = data.transcript_chunks_completed || 0;
    }
    
    const interval = setInterval(async () => {
      try {
        // Poll lightweight chunk progress endpoint
        const progress = await transcriptApi.getChunkProgress(id);
        
        // Update data with progress info
        setData(prev => ({
          ...prev,
          transcript_status: progress.transcript_status,
          transcript_chunks_total: progress.chunks_total,
          transcript_chunks_completed: progress.chunks_completed,
          transcript_current_chunk: progress.current_chunk,
          transcript_active_chunks: progress.active_chunks || [],
        }));
        
        // If chunks_completed changed, refresh transcript data
        if (progress.chunks_completed !== prevChunksCompleted.current) {
          prevChunksCompleted.current = progress.chunks_completed;
          loadTranscriptData();
        }
        
        // If status changed to COMPLETED, stop polling and refresh session data
        if (progress.transcript_status === 'COMPLETED') {
          loadSessionData();
        }
      } catch (e) {
        console.error('[Chunk Progress Polling] Error:', e);
        // Fallback to full session data load on error
        loadSessionData();
      }
    }, 3000); // Poll every 3 seconds (more frequent but lightweight)
    
    return () => clearInterval(interval);
  }, [autoRefresh, data?.transcript_status, id, loadTranscriptData, loadSessionData]);

  // Calculate elapsed time for transcript processing (total time from start)
  const [chunkElapsedTime, setChunkElapsedTime] = useState(null);
  useEffect(() => {
    // Show elapsed time if processing or completed (but only if we have a start time)
    if (!data?.transcript_processing_started_at || 
        (data?.transcript_status !== 'PROCESSING' && data?.transcript_status !== 'COMPLETED')) {
      setChunkElapsedTime(null);
      return;
    }

    const updateElapsed = () => {
      // If completed, use completion time, otherwise use current time
      const endTime = data?.transcript_status === 'COMPLETED' && data?.transcript_processing_completed_at
        ? new Date(data.transcript_processing_completed_at)
        : new Date();
      
      const start = new Date(data.transcript_processing_started_at);
      const seconds = Math.floor((endTime - start) / 1000);
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      
      if (minutes === 0) {
        setChunkElapsedTime(`${remainingSeconds}sec`);
      } else if (remainingSeconds === 0) {
        setChunkElapsedTime(`${minutes}min`);
      } else {
        setChunkElapsedTime(`${minutes}min ${remainingSeconds}sec`);
      }
    };

    updateElapsed();
    
    // Only update interval if still processing (not completed)
    if (data?.transcript_status === 'PROCESSING') {
      const interval = setInterval(updateElapsed, 1000);
      return () => clearInterval(interval);
    }
  }, [data?.transcript_status, data?.transcript_processing_started_at, data?.transcript_processing_completed_at]);

  // Calculate estimated time remaining
  const estimatedTimeRemaining = useMemo(() => {
    if (!data?.transcript_current_chunk || !data?.transcript_chunks_completed || data.transcript_chunks_completed === 0 || !data?.transcript_processing_started_at) {
      return null;
    }
    const elapsed = Math.floor((new Date() - new Date(data.transcript_processing_started_at)) / 1000);
    const avgTimePerChunk = elapsed / data.transcript_chunks_completed;
    const remainingChunks = data.transcript_chunks_total - data.transcript_chunks_completed;
    return formatDuration(Math.round(avgTimePerChunk * remainingChunks));
  }, [data?.transcript_current_chunk, data?.transcript_chunks_completed, data?.transcript_chunks_total, data?.transcript_processing_started_at]);

  const transcriptGenSeconds = useMemo(() => {
    if (!data?.processing_started_at || !data?.processing_completed_at) return null;
    return Math.max(0, Math.round((new Date(data.processing_completed_at) - new Date(data.processing_started_at)) / 1000));
  }, [data]);

  // Load report status
  const loadReportStatus = useCallback(async () => {
    if (!id || !user) return;
    
    // #region agent log
    fetch('http://localhost:7242/ingest/047145a0-dae8-4b06-a8da-014b623ea31d',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'[id].new.jsx:216',message:'loadReportStatus entry',data:{session_id:id},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'})}).catch(()=>{});
    // #endregion
    
    try {
      setIsLoadingReportStatus(true);
      const status = await reportApi.getSessionReportStatus(id);
      
      // #region agent log
      fetch('http://localhost:7242/ingest/047145a0-dae8-4b06-a8da-014b623ea31d',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'[id].new.jsx:221',message:'status response received',data:{session_id:id,status:status?.status,report_id:status?.report_id,has_status:!!status},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'})}).catch(()=>{});
      // #endregion
      
      // Simply use the backend status - old reports are deleted, so no need for complex timestamp checking
      setReportStatus(status);
      
      // If report is completed, load the report content and set report_url
      if (status.status === 'COMPLETED') {
        // #region agent log
        fetch('http://localhost:7242/ingest/047145a0-dae8-4b06-a8da-014b623ea31d',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'[id].new.jsx:227',message:'status is COMPLETED, loading report',data:{session_id:id,status:status.status},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'})}).catch(()=>{});
        // #endregion
        try {
          // First, reload session data to get latest markdown fields
          // This ensures we always use the most recent manually edited content
          const sessionRes = await transcriptApi.getSessionById(id);
          const normalized = normalizeData(sessionRes);
          
          // ALWAYS use markdown fields from session data if they exist (these are the source of truth)
          // Only fall back to report file if session fields are empty
          const sessionAtGlance = normalized?.session_at_glance_md || normalized?.sessionAtGlanceMd || '';
          const fullReport = normalized?.full_report_md || normalized?.fullReportMd || '';
          const speakers = normalized?.speakers_md || normalized?.speakersMd || '';
          const inDepth = normalized?.in_depth_md || normalized?.inDepthMd || '';
          
          // Set markdown fields from session data immediately (these take priority)
          if (sessionAtGlance) {
            setMdSessionAtGlance(sessionAtGlance);
          }
          if (fullReport) {
            setMdFullReport(fullReport);
          }
          if (speakers) {
            setMdSpeakers(speakers);
          }
          if (inDepth) {
            setMdInDepth(inDepth);
          }
          
          const report = await reportApi.getSessionReport(id);
          
          // #region agent log
          fetch('http://localhost:7242/ingest/047145a0-dae8-4b06-a8da-014b623ea31d',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'[id].new.jsx:229',message:'report loaded successfully',data:{session_id:id,has_report:!!report,has_data:!!report?.data,graph_path:report?.graph_path},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'})}).catch(()=>{});
          // #endregion
          console.log('[loadReportStatus] Full report response:', report);
          console.log('[loadReportStatus] report.data:', report.data);
          console.log('[loadReportStatus] report.data keys:', report.data ? Object.keys(report.data) : 'no data');
          console.log('[loadReportStatus] report.data.Speakers:', report.data?.Speakers);
          console.log('[loadReportStatus] report.data type:', typeof report.data);
          
          // Check if report.data is the data object or if it's nested
          const reportDataToStore = report.data?.data || report.data;
          console.log('[loadReportStatus] reportDataToStore:', reportDataToStore);
          console.log('[loadReportStatus] reportDataToStore.Speakers:', reportDataToStore?.Speakers);
          
          setReportData(reportDataToStore); // Store report data for display
          
          // Only populate from report file if session markdown fields are empty
          // Session markdown fields are the source of truth - they override report file data
          if (report.data && report.data['Session report']) {
            console.log('[loadReportStatus] Found Session report section');
            const sessionReport = report.data['Session report'];
            
            // Populate "Session at a glance" from report file ONLY if session field is empty
            if (!sessionAtGlance && sessionReport['Session at a glance']) {
              console.log('[loadReportStatus] Session at a glance not in session, populating from report');
              const atGlance = sessionReport['Session at a glance'];
              let atGlanceMarkdown = '';
              
              if (atGlance.Summary) {
                atGlanceMarkdown += `## Summary\n\n${atGlance.Summary}\n\n`;
              }
              if (atGlance.Keypoints) {
                atGlanceMarkdown += `## Keypoints\n\n${atGlance.Keypoints}\n\n`;
              }
              if (atGlance.Speakers) {
                atGlanceMarkdown += `## Speakers\n\n${atGlance.Speakers}\n\n`;
              }
              
              if (atGlanceMarkdown) {
                console.log('[loadReportStatus] Setting mdSessionAtGlance from report, length:', atGlanceMarkdown.length);
                setMdSessionAtGlance(atGlanceMarkdown.trim());
              }
            } else if (sessionAtGlance) {
              console.log('[loadReportStatus] Using session_at_glance_md from session (source of truth)');
            }
            
            // Populate "Full session report" from report file ONLY if session field is empty
            if (!fullReport && sessionReport['Full session report']) {
              console.log('[loadReportStatus] Full report not in session, populating from report, length:', sessionReport['Full session report'].length);
              setMdFullReport(sessionReport['Full session report']);
            } else if (fullReport) {
              console.log('[loadReportStatus] Using full_report_md from session (source of truth)');
            }
            
            // Populate "Session transcript (report excerpt)" - this is always from report file
            if (sessionReport['Session transcript']) {
              console.log('[loadReportStatus] Found Session transcript, length:', sessionReport['Session transcript'].length);
              setMdTranscript(sessionReport['Session transcript']);
            }
          }
          
          // Populate Speakers tab from report file ONLY if session field is empty
          if (!speakers && report.data && report.data.Speakers) {
            console.log('[loadReportStatus] Speakers not in session, populating from report');
            const speakersData = report.data.Speakers;
            let speakersMarkdown = '';
            
            Object.keys(speakersData).forEach(speakerName => {
              const speakerData = speakersData[speakerName];
              speakersMarkdown += `## ${speakerName}\n\n`;
              
              Object.keys(speakerData).forEach(argumentName => {
                const argumentData = speakerData[argumentName];
                speakersMarkdown += `### ${argumentName}\n\n`;
                
                if (argumentData.Explanation) {
                  speakersMarkdown += `**Explanation:** ${argumentData.Explanation}\n\n`;
                }
                if (argumentData.Evidence) {
                  speakersMarkdown += `**Evidence:** ${argumentData.Evidence}\n\n`;
                }
                if (argumentData['Major discussion point']) {
                  speakersMarkdown += `**Major discussion point:** ${argumentData['Major discussion point']}\n\n`;
                }
                if (argumentData['Agreed with']) {
                  speakersMarkdown += `**Agreed with:** ${Array.isArray(argumentData['Agreed with']) ? argumentData['Agreed with'].join(', ') : argumentData['Agreed with']}\n\n`;
                }
                if (argumentData['Agreed on']) {
                  speakersMarkdown += `**Agreed on:** ${argumentData['Agreed on']}\n\n`;
                }
                if (argumentData['Disagreed with']) {
                  speakersMarkdown += `**Disagreed with:** ${Array.isArray(argumentData['Disagreed with']) ? argumentData['Disagreed with'].join(', ') : argumentData['Disagreed with']}\n\n`;
                }
                if (argumentData['Disagreed on']) {
                  speakersMarkdown += `**Disagreed on:** ${argumentData['Disagreed on']}\n\n`;
                }
                
                speakersMarkdown += '\n';
              });
              
              speakersMarkdown += '\n';
            });
            
            if (speakersMarkdown) {
              console.log('[loadReportStatus] Setting mdSpeakers from report, length:', speakersMarkdown.length);
              setMdSpeakers(speakersMarkdown.trim());
            }
          } else if (speakers) {
            console.log('[loadReportStatus] Using speakers_md from session (source of truth)');
          }
          
          // Populate In-depth analysis from report file ONLY if session field is empty
          if (!inDepth && report.data && report.data['Additional analysis']) {
            console.log('[loadReportStatus] In-depth not in session, populating from report');
            // Parse additional analysis if needed
            // For now, just log that we found it
          } else if (inDepth) {
            console.log('[loadReportStatus] Using in_depth_md from session (source of truth)');
          }
          
          // Set report_url for frontend display
          if (report.path) {
            // Construct URL to access report
            const reportUrl = `/api/reports/sessions/${id}/report`;
            setData(prev => prev ? { ...prev, report_url: reportUrl } : null);
          }
          
          // Set knowledge graph URL from report
          if (report.graph_path) {
            console.log('[loadReportStatus] Found graph_path in report:', report.graph_path);
            setKnowledgeGraphUrl(report.graph_path);
          } else if (reportDataToStore?.graph_path) {
            console.log('[loadReportStatus] Found graph_path in report data:', reportDataToStore.graph_path);
            setKnowledgeGraphUrl(reportDataToStore.graph_path);
          } else {
            console.log('[loadReportStatus] No graph_path found, keeping placeholder');
            // Keep the default placeholder if no graph_path is available
          }
        } catch (e) {
          // #region agent log
          fetch('http://localhost:7242/ingest/047145a0-dae8-4b06-a8da-014b623ea31d',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'[id].new.jsx:351',message:'error loading report content',data:{session_id:id,error:e?.message,status_code:e?.response?.status},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'})}).catch(()=>{});
          // #endregion
          console.error('Failed to load report content:', e);
          setReportData(null);
        }
      } else {
        // #region agent log
        fetch('http://localhost:7242/ingest/047145a0-dae8-4b06-a8da-014b623ea31d',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'[id].new.jsx:355',message:'status is not COMPLETED',data:{session_id:id,status:status?.status},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
        // #endregion
        setReportData(null); // Clear report data if not completed
      }
    } catch (e) {
      // #region agent log
      fetch('http://localhost:7242/ingest/047145a0-dae8-4b06-a8da-014b623ea31d',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'[id].new.jsx:358',message:'error in loadReportStatus',data:{session_id:id,error:e?.message,status_code:e?.response?.status,is_404:e?.response?.status===404},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
      // #endregion
      // Report might not exist yet, which is OK
      if (e.response?.status !== 404) {
        console.error('Failed to load report status:', e);
      }
      setReportStatus(null);
    } finally {
      setIsLoadingReportStatus(false);
    }
  }, [id, user]);

  // Auto-refresh polling when report is processing or pending
  useEffect(() => {
    if (!autoRefreshReport || !reportStatus) return;
    if (reportStatus.status !== 'PROCESSING' && reportStatus.status !== 'PENDING') return;
    
    const interval = setInterval(() => {
      loadReportStatus();
    }, 3000); // Poll every 3 seconds
    
    return () => clearInterval(interval);
  }, [autoRefreshReport, reportStatus?.status, loadReportStatus]);

  // Handler for starting transcript generation
  const handleStartTranscript = async () => {
    setIsGenerating(true);
    try {
      await transcriptApi.startTranscriptGeneration(id, selectedProvider);
      setNotif({
        type: 'success',
        title: 'Transcript generation started',
        message: `Using ${TRANSCRIPT_PROVIDERS.find(p => p.value === selectedProvider)?.label || selectedProvider} provider`
      });
      // Reload session data to show updated status
      setTimeout(() => {
        loadSessionData();
      }, 2000);
    } catch (e) {
      setNotif({ type: 'error', title: 'Failed to start transcript', message: e.message });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRegenerateClick = () => {
    setShowRegenerateModal(true);
  };

  const handleConfirmRegenerate = async () => {
    setIsRegenerating(true);
    try {
      // Step 1: Delete old transcript
      await transcriptApi.regenerateTranscript(id);
      
      // Step 2: Start new generation with selected provider
      await transcriptApi.startTranscriptGeneration(id, selectedProvider);
      
      setNotif({
        type: 'success',
        title: 'Transcript regeneration started',
        message: `Old transcript deleted. Generating new one with ${TRANSCRIPT_PROVIDERS.find(p => p.value === selectedProvider)?.label || selectedProvider}`
      });
      
      // Close modal and reload data
      setShowRegenerateModal(false);
      
      // Reload session to show updated status
      setTimeout(() => {
        loadSessionData();
      }, 1500);
    } catch (e) {
      setNotif({ 
        type: 'error', 
        title: 'Failed to regenerate transcript', 
        message: e.response?.data?.detail || e.message 
      });
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleDownloadMedia = async () => {
    setIsDownloading(true);
    try {
      await transcriptApi.downloadSessionMedia(id);
      
      const filename = data.original_filename || `session_${id}_media`;
      setNotif({
        type: 'success',
        title: 'Download started',
        message: `Downloading ${filename}`
      });
    } catch (e) {
      console.error('Download error:', e);
      setNotif({ 
        type: 'error', 
        title: 'Failed to download media', 
        message: e.message || e.response?.data?.detail || 'Media file not available'
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadPdf = async () => {
    setIsDownloadingPdf(true);
    try {
      await reportApi.downloadSessionReportPdf(id);
      setNotif({
        type: 'success',
        title: 'PDF Downloaded',
        message: 'Report has been downloaded as PDF'
      });
    } catch (e) {
      console.error('PDF download error:', e);
      setNotif({ 
        type: 'error', 
        title: 'Failed to download PDF', 
        message: e.message || e.response?.data?.detail || 'Could not generate PDF'
      });
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const handleGenerateReport = async (config = null) => {
    setIsGeneratingReport(true);
    try {
      // If regenerating (report exists), delete old report data first
      const isRegenerating = reportStatus?.status === 'COMPLETED' || data.report_url;
      
      if (isRegenerating) {
        // Step 1: Delete old report (similar to transcript regeneration)
        await reportApi.regenerateReport(id);
        
        // Clear local state
        setReportData(null);
        setData(prev => prev ? { ...prev, report_url: null } : null);
        // Clear markdown fields when regenerating
        setMdSessionAtGlance('');
        setMdFullReport('');
        setMdTranscript('');
        setMdSpeakers('');
        setMdInDepth('');

        // Reload session from server to align with cleared backend state
        await loadSessionData();
      }
      
      // Ensure auto-refresh is enabled
      setAutoRefreshReport(true);
      
      // Build options object
      const options = {};
      if (config?.llm_model) {
        options.llm_model = config.llm_model;
      }
      if (config?.custom_prompts) {
        options.custom_prompts = config.custom_prompts;
      }
      
      // Step 2: Start new generation
      const response = await reportApi.generateSessionReport(id, options);
      
      setNotif({
        type: 'success',
        title: isRegenerating ? 'Report regeneration started' : 'Report generation started',
        message: `Old report deleted. ${isRegenerating ? 'Generating new report' : 'Report generation has been queued'}. Status: ${response.status || 'QUEUED'}`
      });
      
      // Reload session data and report status
      loadSessionData();
      setTimeout(() => {
        loadReportStatus();
      }, 1500);
      
      // Enable buttons - status will be updated by polling
      setIsGeneratingReport(false);
    } catch (e) {
      setNotif({ 
        type: 'error', 
        title: 'Failed to generate report', 
        message: e.response?.data?.detail || e.message 
      });
      setIsGeneratingReport(false);
      // Reset status on error
      setReportStatus(prev => prev ? { ...prev, status: 'FAILED' } : null);
    }
  };

  const handleReportConfigSubmit = async (customPrompts, llmModel) => {
    const config = {
      custom_prompts: customPrompts,
      llm_model: llmModel
    };
    // Wait for report generation to start before closing modal
    await handleGenerateReport(config);
    // Close modal after generation is initiated
    setShowReportConfigModal(false);
  };

  // Determine publish destination based on event or session
  const getPublishDestination = useCallback(() => {
    if (!data) return null;
    
    // Priority 1: Check session's own faicon_session_id or wp_session_id
    if (data.faicon_session_id) {
      return 'faicon';
    }
    if (data.wp_session_id) {
      return 'wordpress';
    }
    
    // Priority 2: Check event's faicon_id or wp_id
    if (data.event_faicon_id) {
      return 'faicon';
    }
    if (data.event_wp_id) {
      return 'wordpress';
    }
    
    return null;
  }, [data]);

  const handlePublishClick = () => {
    const destination = getPublishDestination();
    if (!destination) {
      setNotif({
        type: 'error',
        title: 'Cannot publish',
        message: 'This session is not linked to a FAICON or WordPress event. Please link it to an event first.'
      });
      return;
    }
    setPublishDestination(destination);
    setShowPublishModal(true);
  };

  const handleConfirmPublish = async () => {
    if (!publishDestination || !id) return;
    
    setIsPublishing(true);
    try {
      let result;
      
      if (publishDestination === 'faicon') {
        result = await reportApi.publishToFaicon(id);
      } else {
        result = await reportApi.publishToWordPress(id, 'v2');
      }
      
      if (result.published_url) {
        // Update session data with published URL
        setData(prev => prev ? { ...prev, url: result.published_url } : null);
        
        // Reload session data to get updated URL
        await loadSessionData();
        
        setNotif({
          type: 'success',
          title: 'Report published successfully',
          message: `Report has been published to ${publishDestination === 'faicon' ? 'FAICON' : 'WordPress'}.`
        });
      } else {
        setNotif({
          type: 'warning',
          title: 'Publish completed',
          message: 'Report was published but no URL was returned.'
        });
      }
      
      setShowPublishModal(false);
    } catch (e) {
      setNotif({
        type: 'error',
        title: 'Failed to publish report',
        message: e.response?.data?.detail || e.message || 'An error occurred while publishing the report.'
      });
    } finally {
      setIsPublishing(false);
    }
  };

  const handleUpdatePublish = () => {
    const destination = getPublishDestination();
    if (!destination) {
      setNotif({
        type: 'error',
        title: 'Cannot update publish',
        message: 'This session is not linked to a FAICON or WordPress event.'
      });
      return;
    }
    setPublishDestination(destination);
    setShowPublishModal(true);
  };

  const toggleSpeaker = (speakerName) => {
    setExpandedSpeakers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(speakerName)) {
        newSet.delete(speakerName);
      } else {
        newSet.add(speakerName);
      }
      return newSet;
    });
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      await transcriptApi.deleteSession(id);
      
      setNotif({
        type: 'success',
        title: 'Session deleted',
        message: 'Session has been permanently deleted'
      });
      
      // Close modal and redirect to sessions list after a short delay
      setShowDeleteModal(false);
      setTimeout(() => {
        navigate('/sessions');
      }, 1500);
    } catch (e) {
      setNotif({ 
        type: 'error', 
        title: 'Failed to delete session', 
        message: e.response?.data?.detail || e.message 
      });
      setIsDeleting(false);
    }
  };

  if (!user) {
    return (
      <LoginForm 
        onSSOLogin={getAuth}
        ssoLoading={loading}
        ssoError={error}
      />
    );
  }

  if (!data) {
    return <div className="min-h-screen bg-gray-50"><div className="max-w-5xl mx-auto px-4 py-8">Loading...</div></div>;
  }

  const statusClass = data.status === 'COMPLETED' ? 'bg-green-50 border-green-200 text-green-700' : data.status === 'FAILED' ? 'bg-red-50 border-red-200 text-red-700' : data.status === 'IN_PROGRESS' ? 'bg-yellow-50 border-yellow-200 text-yellow-700' : 'bg-gray-50 border-gray-200 text-gray-700';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl font-bold text-gray-900">{data.title || 'Session'}</h1>
                <span className={`px-2 py-0.5 rounded-full border text-xs ${statusClass}`}>{data.status || 'N/A'}</span>
                {data.event_id && (
                  <Link to={`/events/${data.event_id}`} className="text-xs px-2 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700">
                    {data.event_title || `Event #${data.event_id}`}
                  </Link>
                )}
                {data.user_id && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-50 border border-gray-200 text-gray-700">
                    {data.user_name || data.user_email || `User #${data.user_id}`}
                  </span>
                )}
              </div>
              {data.description && <p className="text-gray-600 mt-1">{data.description}</p>}
            </div>
          </div>
        </div>
      </div>

      {/* Error Notification for FAILED status */}
      {data.status === 'FAILED' && data.error_message && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-red-800">Transcript Generation Failed</h3>
                <div className="mt-1 text-sm text-red-700">
                  {data.error_message}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Transcript + Report Tabs */}
        <div className="lg:col-span-2 space-y-6">
          {/* Transcript section (separate from Report tabs) */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Transcript</h3>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span className="px-2 py-0.5 rounded-full bg-gray-50 border border-gray-200">Provider: {data.transcript_provider || '—'}</span>
                {transcriptGenSeconds != null && <span className="px-2 py-0.5 rounded-full bg-gray-50 border border-gray-200">Gen: {transcriptGenSeconds}s</span>}
                {data.transcript_status === 'COMPLETED' && (
                  <Link to={`/sessions/${id}/edit`} className="inline-flex items-center px-3 py-1.5 rounded-md border bg-white text-gray-700 border-gray-300 hover:bg-gray-50">
                    <PencilSquareIcon className="h-4 w-4 mr-1" />
                    Edit
                  </Link>
                )}
              </div>
            </div>
            <div className="p-5 max-h-[600px] overflow-y-auto">
              <TranscriptDisplay 
                segments={transcriptSegments} 
                loading={loadingTranscript}
              />
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="border-b px-4">
              <div className="flex gap-2 items-center justify-between overflow-x-auto">
                <div className="flex gap-2">
                  <button onClick={() => setActiveTab('report')} className={`px-4 py-3 text-sm font-medium border-b-2 ${activeTab === 'report' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-600 hover:text-gray-800'}`}>Session report</button>
                  <button onClick={() => setActiveTab('speakers')} className={`px-4 py-3 text-sm font-medium border-b-2 ${activeTab === 'speakers' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-600 hover:text-gray-800'}`}>Speakers</button>
                  <button onClick={() => setActiveTab('graph')} className={`px-4 py-3 text-sm font-medium border-b-2 ${activeTab === 'graph' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-600 hover:text-gray-800'}`}>Knowledge graph</button>
                  <button onClick={() => setActiveTab('analysis')} className={`px-4 py-3 text-sm font-medium border-b-2 ${activeTab === 'analysis' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-600 hover:text-gray-800'}`}>In-depth analysis</button>
                </div>
                <Link to={`/sessions/${id}/report/edit`} className="inline-flex items-center px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50 text-sm whitespace-nowrap">
                  <PencilSquareIcon className="h-4 w-4 mr-1" />
                  Edit Report
                </Link>
              </div>
            </div>
            <div className="p-5">
              {activeTab === 'report' && (
                <div className="space-y-6">
                  {/* Show message if no report generated yet */}
                  {!reportData && reportStatus?.status !== 'PROCESSING' && reportStatus?.status !== 'PENDING' && (
                    <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200 rounded-xl shadow-sm p-5">
                      <div className="flex items-center justify-between">
                  <div>
                          <h4 className="text-base font-semibold text-yellow-900 mb-1">No report generated yet</h4>
                          <p className="text-sm text-yellow-700">Generate a report to see the content here.</p>
                        </div>
                        {data.transcript_status === 'COMPLETED' && (
                          <button
                            onClick={handleGenerateReport}
                            disabled={isGeneratingReport}
                            className="px-4 py-2 rounded-lg border text-sm bg-white text-gray-700 border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {isGeneratingReport ? 'Generating...' : 'Generate Report'}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Session at a glance */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="bg-gradient-to-r from-gray-50 to-gray-100/50 px-6 py-4 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-lg bg-primary-100 flex items-center justify-center">
                            <svg className="h-5 w-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                          <div>
                            <h4 className="text-lg font-semibold text-gray-900">Session at a glance</h4>
                            <p className="text-xs text-gray-500 mt-0.5">Summary, keypoints, and speakers overview</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="p-6">
                      <ReadMoreText text={mdSessionAtGlance} maxLength={800} className="" />
                    </div>
                  </div>
                  
                  {/* Full session report */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="bg-gradient-to-r from-gray-50 to-gray-100/50 px-6 py-4 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                            <svg className="h-5 w-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                          <div>
                            <h4 className="text-lg font-semibold text-gray-900">Full session report</h4>
                            <p className="text-xs text-gray-500 mt-0.5">Comprehensive analysis and detailed insights</p>
                          </div>
                        </div>
                        {/* Download PDF Button */}
                        {reportStatus?.status === 'COMPLETED' && (
                          <button
                            onClick={handleDownloadPdf}
                            disabled={isDownloadingPdf}
                            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Download report as PDF"
                          >
                            {isDownloadingPdf ? (
                              <>
                                <ArrowPathIcon className="h-4 w-4 animate-spin" />
                                <span>Generating...</span>
                              </>
                            ) : (
                              <>
                                <ArrowDownTrayIcon className="h-4 w-4" />
                                <span>Download PDF</span>
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="p-6">
                      <ReadMoreText text={mdFullReport} maxLength={1000} className="" />
                    </div>
                  </div>
                </div>
              )}
              {activeTab === 'speakers' && (
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-xl px-6 py-4 border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                          <svg className="h-5 w-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </div>
                <div>
                          <h4 className="text-lg font-semibold text-gray-900">Speakers Analysis</h4>
                          <p className="text-xs text-gray-500 mt-0.5">Detailed breakdown of each speaker's arguments and positions</p>
                        </div>
                      </div>
                  </div>
                  </div>
                  
                  <div className="space-y-8">
                      {(() => {
                        console.log('[Speakers Tab] reportData:', reportData);
                        console.log('[Speakers Tab] reportData.Speakers:', reportData?.Speakers);
                        return null;
                      })()}
                      {reportData && reportData.Speakers ? (
                        Object.keys(reportData.Speakers).map((speakerName, speakerIndex) => {
                          const speakerData = reportData.Speakers[speakerName];
                          const argumentsList = Object.keys(speakerData);
                          
                          const isExpanded = expandedSpeakers.has(speakerName);
                          
                          return (
                            <div key={speakerName} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                              {/* Speaker Header - Clickable */}
                              <button
                                onClick={() => toggleSpeaker(speakerName)}
                                className="w-full bg-gradient-to-r from-purple-50 to-indigo-50 px-6 py-5 border-b border-gray-200 hover:from-purple-100 hover:to-indigo-100 transition-all duration-200 text-left cursor-pointer group"
                              >
                                <div className="flex items-center gap-4">
                                  <div className="h-16 w-16 rounded-full bg-purple-100 border-2 border-purple-200 flex items-center justify-center text-purple-700 font-bold text-2xl flex-shrink-0 group-hover:bg-purple-200 transition-colors">
                                    {speakerName.charAt(0).toUpperCase()}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <h3 className="text-2xl font-bold text-gray-900 mb-1">{speakerName}</h3>
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full font-medium">
                                        {argumentsList.length} {argumentsList.length === 1 ? 'argument' : 'arguments'}
                                      </span>
                                      {!isExpanded && (
                                        <span className="text-xs text-gray-500 italic">Click to expand</span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex-shrink-0 transform transition-transform group-hover:scale-110">
                                    {isExpanded ? (
                                      <ChevronUpIcon className="h-6 w-6 text-gray-600" />
                                    ) : (
                                      <ChevronDownIcon className="h-6 w-6 text-gray-600" />
                                    )}
                                  </div>
                                </div>
                              </button>
                              
                              {/* Arguments Container - Conditionally Rendered */}
                              {isExpanded && (
                                <div className="p-6 space-y-6 bg-gray-50/50 transition-all duration-300">
                                {argumentsList.map((argumentName, argIndex) => {
                                  const argumentData = speakerData[argumentName];
                                  return (
                                    <React.Fragment key={argumentName}>
                                      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                                        {/* Argument Header */}
                                        <div className="bg-gray-50 px-5 py-4 border-b border-gray-200">
                                          <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                              <div className="flex items-center gap-2 mb-2">
                                                <div className="h-2 w-2 rounded-full bg-purple-400"></div>
                                                <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Argument {argIndex + 1}</span>
                                              </div>
                                              <h4 className="text-lg font-bold text-gray-900 leading-tight">{argumentName}</h4>
                                            </div>
                                          </div>
                                        </div>
                                        
                                        {/* Argument Content */}
                                        <div className="p-5 space-y-4">
                                          {argumentData.Explanation && (
                                            <div className="bg-blue-50/50 border-l-2 border-blue-300 rounded-r-lg p-4">
                                              <div className="flex items-start gap-3">
                                                <div className="h-8 w-8 rounded-lg bg-blue-200 flex items-center justify-center flex-shrink-0">
                                                  <svg className="h-5 w-5 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                  </svg>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                  <div className="text-xs font-semibold text-gray-700 uppercase mb-2 tracking-wide">Explanation</div>
                                                  <div className="text-sm text-gray-700 leading-relaxed">{argumentData.Explanation}</div>
                                                </div>
                                              </div>
                                            </div>
                                          )}
                                          
                                          {argumentData.Evidence && (
                                            <div className="bg-green-50/50 border-l-2 border-green-300 rounded-r-lg p-4">
                                              <div className="flex items-start gap-3">
                                                <div className="h-8 w-8 rounded-lg bg-green-200 flex items-center justify-center flex-shrink-0">
                                                  <svg className="h-5 w-5 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                  </svg>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                  <div className="text-xs font-semibold text-gray-700 uppercase mb-2 tracking-wide">Evidence</div>
                                                  <div className="text-sm text-gray-700 leading-relaxed">{argumentData.Evidence}</div>
                                                </div>
                                              </div>
                                            </div>
                                          )}
                                          
                                          {argumentData['Major discussion point'] && (
                                            <div className="bg-amber-50/50 border-l-2 border-amber-300 rounded-r-lg p-4">
                                              <div className="flex items-start gap-3">
                                                <div className="h-8 w-8 rounded-lg bg-amber-200 flex items-center justify-center flex-shrink-0">
                                                  <svg className="h-5 w-5 text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h10m-7 4h7" />
                                                  </svg>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                  <div className="text-xs font-semibold text-gray-700 uppercase mb-2 tracking-wide">Major Discussion Point</div>
                                                  <div className="text-sm text-gray-700 leading-relaxed font-medium">{argumentData['Major discussion point']}</div>
                                                </div>
                                              </div>
                                            </div>
                                          )}
                                          
                                          {(argumentData['Agreed with'] || argumentData['Agreed on']) && (
                                            <div className="bg-emerald-50/50 border-l-2 border-emerald-300 rounded-r-lg p-4">
                                              <div className="flex items-start gap-3">
                                                <div className="h-8 w-8 rounded-lg bg-emerald-200 flex items-center justify-center flex-shrink-0">
                                                  <svg className="h-5 w-5 text-emerald-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                                                  </svg>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                  <div className="text-xs font-semibold text-gray-700 uppercase mb-2 tracking-wide">Agreements</div>
                                                  {argumentData['Agreed with'] && (
                                                    <div className="text-sm text-gray-700 mb-2">
                                                      <span className="font-semibold">Agreed with:</span> {Array.isArray(argumentData['Agreed with']) ? argumentData['Agreed with'].join(', ') : argumentData['Agreed with']}
                                                    </div>
                                                  )}
                                                  {argumentData['Agreed on'] && (
                                                    <div className="text-sm text-gray-700">
                                                      <span className="font-semibold">Agreed on:</span> {argumentData['Agreed on']}
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          )}
                                          
                                          {(argumentData['Disagreed with'] || argumentData['Disagreed on']) && (
                                            <div className="bg-red-50/50 border-l-2 border-red-300 rounded-r-lg p-4">
                                              <div className="flex items-start gap-3">
                                                <div className="h-8 w-8 rounded-lg bg-red-200 flex items-center justify-center flex-shrink-0">
                                                  <svg className="h-5 w-5 text-red-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                  </svg>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                  <div className="text-xs font-semibold text-gray-700 uppercase mb-2 tracking-wide">Disagreements</div>
                                                  {argumentData['Disagreed with'] && (
                                                    <div className="text-sm text-gray-700 mb-2">
                                                      <span className="font-semibold">Disagreed with:</span> {Array.isArray(argumentData['Disagreed with']) ? argumentData['Disagreed with'].join(', ') : argumentData['Disagreed with']}
                                                    </div>
                                                  )}
                                                  {argumentData['Disagreed on'] && (
                                                    <div className="text-sm text-gray-700">
                                                      <span className="font-semibold">Disagreed on:</span> {argumentData['Disagreed on']}
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      {argIndex < argumentsList.length - 1 && (
                                        <div className="my-8 flex items-center justify-center">
                                          <div className="flex-1 border-t-2 border-gray-400"></div>
                                          <div className="mx-4 w-2 h-2 rounded-full bg-gray-400"></div>
                                          <div className="flex-1 border-t-2 border-gray-400"></div>
                                        </div>
                                      )}
                                    </React.Fragment>
                                  );
                                })}
                                </div>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                          <ReadMoreText text={mdSpeakers} maxLength={1500} className="" />
                        </div>
                      )}
                    </div>
                </div>
              )}
              {activeTab === 'graph' && (
                <div>
                  <div className="border rounded-lg overflow-hidden">
                    <iframe title="knowledge-graph" src={knowledgeGraphUrl} className="w-full h-96" />
                  </div>
                  <div className="flex justify-end mt-3 gap-2">
                    <a href={knowledgeGraphUrl} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 rounded-md border bg-white text-gray-700 border-gray-300 hover:bg-gray-50 text-sm">Open Graph</a>
                  </div>
                </div>
              )}
              {activeTab === 'analysis' && (
                <div>
                  {reportData && reportData['Additional analysis'] ? (
                    <div className="space-y-6">
                      {(() => {
                        const additionalAnalysis = reportData['Additional analysis'];
                        return (
                          <>
                            {/* Agreements Section */}
                            {additionalAnalysis.Agreements && (
                              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                <h4 className="text-lg font-semibold text-gray-900 mb-4">Agreements</h4>
                                
                                {additionalAnalysis.Agreements['Agreement points'] && (
                                  <div className="mb-6">
                                    <h5 className="text-sm font-semibold text-gray-700 mb-3">Agreement Points</h5>
                                    <div className="space-y-4">
                                      {Object.keys(additionalAnalysis.Agreements['Agreement points']).map((agreementKey) => {
                                        const agreement = additionalAnalysis.Agreements['Agreement points'][agreementKey];
                                        return (
                                          <div key={agreementKey} className="bg-green-50 border-l-4 border-green-500 rounded-r-lg p-4">
                                            <h6 className="font-semibold text-gray-900 mb-2">{agreementKey}</h6>
                                            {agreement.Speakers && (
                                              <p className="text-xs text-gray-600 mb-2">
                                                <span className="font-medium">Speakers:</span> {Array.isArray(agreement.Speakers) ? agreement.Speakers.join(', ') : agreement.Speakers}
                                              </p>
                                            )}
                                            {agreement.Arguments && (
                                              <div className="text-sm text-gray-700 mb-2 whitespace-pre-line">{agreement.Arguments}</div>
                                            )}
                                            {agreement.Summary && (
                                              <div className="text-sm text-gray-800 font-medium mt-2">{agreement.Summary}</div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                                
                                {additionalAnalysis.Agreements['Similar viewpoints'] && (
                                  <div className="mb-6">
                                    <h5 className="text-sm font-semibold text-gray-700 mb-3">Similar Viewpoints</h5>
                                    <div className="space-y-3">
                                      {Object.keys(additionalAnalysis.Agreements['Similar viewpoints']).map((viewpointKey) => {
                                        const viewpoint = additionalAnalysis.Agreements['Similar viewpoints'][viewpointKey];
                                        return (
                                          <div key={viewpointKey} className="bg-blue-50 border-l-4 border-blue-500 rounded-r-lg p-4">
                                            <h6 className="font-semibold text-gray-900 mb-2">{viewpointKey}</h6>
                                            {viewpoint.Speakers && (
                                              <p className="text-xs text-gray-600 mb-2">
                                                <span className="font-medium">Speakers:</span> {Array.isArray(viewpoint.Speakers) ? viewpoint.Speakers.join(', ') : viewpoint.Speakers}
                                              </p>
                                            )}
                                            {viewpoint.Arguments && (
                                              <div className="text-sm text-gray-700 whitespace-pre-line">{viewpoint.Arguments}</div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                                
                                {additionalAnalysis.Agreements['Unexpected consensus'] && (
                                  <div className="mb-6">
                                    <h5 className="text-sm font-semibold text-gray-700 mb-3">Unexpected Consensus</h5>
                                    <div className="space-y-3">
                                      {Object.keys(additionalAnalysis.Agreements['Unexpected consensus']).map((consensusKey) => {
                                        const consensus = additionalAnalysis.Agreements['Unexpected consensus'][consensusKey];
                                        return (
                                          <div key={consensusKey} className="bg-purple-50 border-l-4 border-purple-500 rounded-r-lg p-4">
                                            <h6 className="font-semibold text-gray-900 mb-2">{consensusKey}</h6>
                                            {consensus.Speakers && (
                                              <p className="text-xs text-gray-600 mb-2">
                                                <span className="font-medium">Speakers:</span> {Array.isArray(consensus.Speakers) ? consensus.Speakers.join(', ') : consensus.Speakers}
                                              </p>
                                            )}
                                            {consensus.Arguments && (
                                              <div className="text-sm text-gray-700 mb-2 whitespace-pre-line">{consensus.Arguments}</div>
                                            )}
                                            {consensus.Explanation && (
                                              <div className="text-sm text-gray-800 font-medium mt-2 italic">{consensus.Explanation}</div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                                
                                {additionalAnalysis.Agreements['Overall assessment'] && (
                                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                    <h5 className="text-sm font-semibold text-gray-700 mb-2">Overall Assessment</h5>
                                    {additionalAnalysis.Agreements['Overall assessment'].Summary && (
                                      <p className="text-sm text-gray-700 mb-2">{additionalAnalysis.Agreements['Overall assessment'].Summary}</p>
                                    )}
                                    {additionalAnalysis.Agreements['Overall assessment']['Consensus level'] && (
                                      <p className="text-xs text-gray-600 italic">{additionalAnalysis.Agreements['Overall assessment']['Consensus level']}</p>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Differences Section */}
                            {additionalAnalysis.Differences && (
                              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                <h4 className="text-lg font-semibold text-gray-900 mb-4">Differences</h4>
                                
                                {additionalAnalysis.Differences['Different viewpoints'] && Object.keys(additionalAnalysis.Differences['Different viewpoints']).length > 0 && (
                                  <div className="mb-6">
                                    <h5 className="text-sm font-semibold text-gray-700 mb-3">Different Viewpoints</h5>
                                    <div className="space-y-3">
                                      {Object.keys(additionalAnalysis.Differences['Different viewpoints']).map((diffKey) => {
                                        const diff = additionalAnalysis.Differences['Different viewpoints'][diffKey];
                                        return (
                                          <div key={diffKey} className="bg-red-50 border-l-4 border-red-500 rounded-r-lg p-4">
                                            <h6 className="font-semibold text-gray-900 mb-2">{diffKey}</h6>
                                            {diff.Speakers && (
                                              <p className="text-xs text-gray-600 mb-2">
                                                <span className="font-medium">Speakers:</span> {Array.isArray(diff.Speakers) ? diff.Speakers.join(', ') : diff.Speakers}
                                              </p>
                                            )}
                                            {diff.Arguments && (
                                              <div className="text-sm text-gray-700 whitespace-pre-line">{diff.Arguments}</div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                                
                                {additionalAnalysis.Differences['Overall assessment'] && (
                                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                    <h5 className="text-sm font-semibold text-gray-700 mb-2">Overall Assessment</h5>
                                    {additionalAnalysis.Differences['Overall assessment'].Summary && (
                                      <p className="text-sm text-gray-700 mb-2">{additionalAnalysis.Differences['Overall assessment'].Summary}</p>
                                    )}
                                    {additionalAnalysis.Differences['Overall assessment']['Disagreement level'] && (
                                      <p className="text-xs text-gray-600 italic">{additionalAnalysis.Differences['Overall assessment']['Disagreement level']}</p>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Takeaways Section */}
                            {additionalAnalysis.Takeaways && (
                              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                <h4 className="text-lg font-semibold text-gray-900 mb-4">Takeaways</h4>
                                
                                {additionalAnalysis.Takeaways['Key takeaways'] && (
                                  <div className="mb-4">
                                    <h5 className="text-sm font-semibold text-gray-700 mb-2">Key Takeaways</h5>
                                    <div className="text-sm text-gray-700 whitespace-pre-line bg-blue-50 border-l-4 border-blue-500 rounded-r-lg p-4">
                                      {additionalAnalysis.Takeaways['Key takeaways']}
                                    </div>
                                  </div>
                                )}
                                
                                {additionalAnalysis.Takeaways['Resolutions and action items'] && (
                                  <div className="mb-4">
                                    <h5 className="text-sm font-semibold text-gray-700 mb-2">Resolutions and Action Items</h5>
                                    <div className="text-sm text-gray-700 whitespace-pre-line bg-green-50 border-l-4 border-green-500 rounded-r-lg p-4">
                                      {additionalAnalysis.Takeaways['Resolutions and action items']}
                                    </div>
                                  </div>
                                )}
                                
                                {additionalAnalysis.Takeaways['Unresolved issues'] && (
                                  <div className="mb-4">
                                    <h5 className="text-sm font-semibold text-gray-700 mb-2">Unresolved Issues</h5>
                                    <div className="text-sm text-gray-700 whitespace-pre-line bg-yellow-50 border-l-4 border-yellow-500 rounded-r-lg p-4">
                                      {additionalAnalysis.Takeaways['Unresolved issues']}
                                    </div>
                                  </div>
                                )}
                                
                                {additionalAnalysis.Takeaways['Suggested compromises'] && (
                                  <div className="mb-4">
                                    <h5 className="text-sm font-semibold text-gray-700 mb-2">Suggested Compromises</h5>
                                    <div className="text-sm text-gray-700 whitespace-pre-line bg-purple-50 border-l-4 border-purple-500 rounded-r-lg p-4">
                                      {additionalAnalysis.Takeaways['Suggested compromises']}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Thought Provoking Comments */}
                            {additionalAnalysis['Thought provoking comments'] && (
                              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                <h4 className="text-lg font-semibold text-gray-900 mb-4">Thought Provoking Comments</h4>
                                <div className="space-y-4">
                                  {Object.keys(additionalAnalysis['Thought provoking comments']).filter(key => key !== 'Overall assessment').map((commentKey) => {
                                    const comment = additionalAnalysis['Thought provoking comments'][commentKey];
                                    return (
                                      <div key={commentKey} className="bg-indigo-50 border-l-4 border-indigo-500 rounded-r-lg p-4">
                                        <div className="text-sm font-semibold text-gray-900 mb-2 italic">"{commentKey}"</div>
                                        {comment.Speaker && (
                                          <p className="text-xs text-gray-600 mb-2">
                                            <span className="font-medium">Speaker:</span> {comment.Speaker}
                                          </p>
                                        )}
                                        {comment.Reason && (
                                          <div className="text-sm text-gray-700 mb-2">
                                            <span className="font-medium">Reason:</span> {comment.Reason}
                                          </div>
                                        )}
                                        {comment.Impact && (
                                          <div className="text-sm text-gray-700">
                                            <span className="font-medium">Impact:</span> {comment.Impact}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                  {additionalAnalysis['Thought provoking comments']['Overall assessment'] && (
                                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-4">
                                      <h5 className="text-sm font-semibold text-gray-700 mb-2">Overall Assessment</h5>
                                      <p className="text-sm text-gray-700">{additionalAnalysis['Thought provoking comments']['Overall assessment']}</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            {/* Follow-up Questions */}
                            {additionalAnalysis['Follow-up questions'] && (
                              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                <h4 className="text-lg font-semibold text-gray-900 mb-4">Follow-up Questions</h4>
                                <div className="space-y-3">
                                  {Object.keys(additionalAnalysis['Follow-up questions']).map((questionKey) => {
                                    const question = additionalAnalysis['Follow-up questions'][questionKey];
                                    return (
                                      <div key={questionKey} className="bg-amber-50 border-l-4 border-amber-500 rounded-r-lg p-4">
                                        <div className="text-sm font-semibold text-gray-900 mb-2">{questionKey}</div>
                                        {question.Speaker && (
                                          <p className="text-xs text-gray-600 mb-2">
                                            <span className="font-medium">Speaker:</span> {question.Speaker}
                                          </p>
                                        )}
                                        {question.Explanation && (
                                          <div className="text-sm text-gray-700">{question.Explanation}</div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  ) : (
                    <div>
                      <div className="mb-2">
                        <h4 className="text-sm font-semibold text-gray-900">In-depth analysis</h4>
                      </div>
                      <ReadMoreText text={mdInDepth} maxLength={1500} className="" />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right: Meta panel */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Meta</h3>
            <dl className="text-sm text-gray-700 space-y-2">
              <div className="grid grid-cols-3 gap-2">
                <dt className="text-gray-500 font-medium">Status</dt>
                <dd className="col-span-2">
                  <span className={`px-2 py-0.5 rounded-full border text-xs ${statusClass}`}>
                    {data.status || 'N/A'}
                  </span>
                </dd>
              </div>
              {data.event_title && (
                <div className="grid grid-cols-3 gap-2">
                  <dt className="text-gray-500">Event</dt>
                  <dd className="col-span-2 truncate">{data.event_title}</dd>
                </div>
              )}
              <div className="grid grid-cols-3 gap-2">
                <dt className="text-gray-500">Provider</dt>
                <dd className="col-span-2">{data.transcript_provider || '—'}</dd>
              </div>
              
              {/* Generate Transcript Button - shown when no transcript exists */}
              {!data.transcript_status && (
                <div className="col-span-3 border-t border-gray-200 pt-3 mt-1">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">Transcript Generation</h4>
                  <div className="space-y-3">
                    <div className="text-sm text-gray-600">
                      No transcript available. Select a provider and generate one.
                    </div>
                    <div>
                      <label className="text-xs text-gray-600 mb-1 block">Provider:</label>
                      <select
                        value={selectedProvider}
                        onChange={(e) => setSelectedProvider(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                      >
                        {TRANSCRIPT_PROVIDERS.map((provider) => (
                          <option key={provider.value} value={provider.value}>
                            {provider.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button
                      onClick={handleStartTranscript}
                      disabled={isGenerating}
                      className="w-full inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isGenerating ? (
                        <>
                          <ClockIcon className="h-5 w-5 mr-2 animate-spin" />
                          Starting...
                        </>
                      ) : (
                        <>
                          <PlayIcon className="h-5 w-5 mr-2" />
                          Generate Transcript
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
              
              {/* Transcript Status Display */}
              {data.transcript_status && (
                <div className="col-span-3 border-t border-gray-200 pt-3 mt-1">
                  <div className="mb-2">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Transcript Generation</h4>
                    <TranscriptStatusDisplay 
                      status={data.transcript_status}
                      startedAt={data.transcript_processing_started_at}
                      completedAt={data.transcript_processing_completed_at}
                      duration={data.transcript_processing_duration}
                      errorMessage={data.transcript_error_message}
                      provider={data.transcript_provider}
                      compact={true}
                    />
                  </div>
                  
                  {/* Chunk Progress Display */}
                  {data.transcript_chunks_total && data.transcript_chunks_total > 1 && data.transcript_status === 'PROCESSING' && (() => {
                    const chunksTotal = data.transcript_chunks_total;
                    const chunksCompleted = data.transcript_chunks_completed || 0;
                    const currentChunk = data.transcript_current_chunk;
                    const progressPercentage = chunksTotal > 0 ? Math.round((chunksCompleted / chunksTotal) * 100) : 0;
                    const isCompleted = data.transcript_status === 'COMPLETED' && chunksCompleted === chunksTotal;

                    // If all completed, show summary only
                    if (isCompleted) {
                      return (
                        <div className="mt-3 p-5 bg-gradient-to-r from-green-50 to-green-100/50 rounded-xl border-2 border-green-400 shadow-md">
                          <div className="flex items-center gap-4">
                            <div className="flex-shrink-0">
                              <CheckCircleIcon className="h-8 w-8 text-green-600" />
                            </div>
                            <div className="flex-1">
                              <h5 className="text-base font-semibold text-green-900 mb-1">
                                Transcript successfully generated
                              </h5>
                              <div className="flex items-center gap-4 text-sm text-gray-700">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">Chunks:</span>
                                  <span className="text-green-700 font-semibold">{chunksTotal}</span>
                                </div>
                                {chunkElapsedTime && (
                                  <div className="flex items-center gap-2 pl-4 border-l border-green-300">
                                    <ClockIcon className="h-4 w-4 text-green-600" />
                                    <span className="font-medium">Total time:</span>
                                    <span className="text-green-700 font-semibold">{chunkElapsedTime}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    // Otherwise, show detailed progress
                    const toggleChunk = (chunkNum) => {
                      setExpandedChunks(prev => {
                        const newSet = new Set(prev);
                        if (newSet.has(chunkNum)) {
                          newSet.delete(chunkNum);
                        } else {
                          newSet.add(chunkNum);
                        }
                        return newSet;
                      });
                    };

                    return (
                      <div className="mt-3">
                        {/* Header with progress stats */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <h5 className="text-sm font-semibold text-gray-700">
                              Chunk Progress
                            </h5>
                            <InformationCircleIcon className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 cursor-help" />
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <div className="text-xs text-gray-500 mb-0.5">Completed</div>
                              <div className="text-xs font-bold text-gray-800">
                                {chunksCompleted}/{chunksTotal}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-gray-500 mb-0.5">Percentage</div>
                              <div className="text-sm font-bold text-blue-600">
                                {progressPercentage}%
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Chunk list - compact, collapsible, no borders */}
                        <div className="space-y-1">
                          {Array.from({ length: chunksTotal }, (_, i) => {
                            const chunkNum = i + 1;
                            // Parse active chunks from API (can be array or JSON string)
                            const activeChunks = Array.isArray(data.transcript_active_chunks) 
                              ? data.transcript_active_chunks 
                              : (data.transcript_active_chunks ? JSON.parse(data.transcript_active_chunks) : []);
                            const isCompleted = chunkNum <= chunksCompleted;
                            const isInProgress = activeChunks.includes(chunkNum);  // Check if in active list
                            const isPending = !isCompleted && !isInProgress;
                            const isExpanded = expandedChunks.has(chunkNum);
                            
                            return (
                              <div key={i} className="bg-gray-50 rounded-md overflow-hidden">
                                {/* Collapsible header */}
                                <button
                                  onClick={() => toggleChunk(chunkNum)}
                                  className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-100 transition-colors text-left"
                                >
                                  <div className="flex items-center gap-2 flex-1 min-w-0">
                                    {/* Status icon */}
                                    <div className="flex-shrink-0">
                                      {isCompleted ? (
                                        <CheckCircleIcon className="h-4 w-4 text-green-600" />
                                      ) : isInProgress ? (
                                        <ArrowPathIcon className="h-4 w-4 text-blue-600 animate-spin" />
                                      ) : (
                                        <ClockIcon className="h-4 w-4 text-gray-400" />
                                      )}
                                    </div>
                                    
                                    {/* Chunk number and status */}
                                    <span className={`text-xs font-medium whitespace-nowrap ${
                                      isCompleted
                                        ? 'text-green-700'
                                        : isInProgress
                                        ? 'text-blue-700'
                                        : 'text-gray-600'
                                    }`}>
                                      Chunk {chunkNum} of {chunksTotal}
                                    </span>
                                    
                                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium whitespace-nowrap ${
                                      isCompleted
                                        ? 'bg-green-200 text-green-800'
                                        : isInProgress
                                        ? 'bg-blue-200 text-blue-800'
                                        : 'bg-gray-200 text-gray-600'
                                    }`}>
                                      {isCompleted ? 'Done' : isInProgress ? 'Processing...' : 'Waiting'}
                                    </span>

                                    {/* Time info for active chunk */}
                                    {isInProgress && chunkElapsedTime && (
                                      <div className="flex items-center gap-1 text-gray-500 ml-auto">
                                        <ClockIcon className="h-3 w-3" />
                                        <span className="text-xs whitespace-nowrap">{chunkElapsedTime}</span>
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Chevron icon */}
                                  <div className="flex-shrink-0 ml-2">
                                    {isExpanded ? (
                                      <ChevronUpIcon className="h-4 w-4 text-gray-400" />
                                    ) : (
                                      <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                                    )}
                                  </div>
                                </button>

                                {/* Expanded content */}
                                {isExpanded && (
                                  <div className="px-3 pb-2 pt-1 border-t border-gray-200">
                                    <div className="flex items-center gap-3 text-xs text-gray-600">
                                      {isCompleted && (
                                        <div className="flex items-center gap-1 text-green-600">
                                          <CheckCircleIcon className="h-3.5 w-3.5" />
                                          <span className="font-medium">Completed</span>
                                        </div>
                                      )}
                                      {isInProgress && (
                                        <>
                                          <div className="flex items-center gap-1 text-blue-600">
                                            <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                                            <span className="font-medium">Active</span>
                                          </div>
                                          {chunkElapsedTime && (
                                            <div className="flex items-center gap-1 text-gray-500">
                                              <ClockIcon className="h-3 w-3" />
                                              <span>{chunkElapsedTime}</span>
                                            </div>
                                          )}
                                        </>
                                      )}
                                      {isPending && (
                                        <div className="flex items-center gap-1 text-gray-400">
                                          <ClockIcon className="h-3.5 w-3.5" />
                                          <span>In queue</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        {/* Footer with time information - compact */}
                        {(currentChunk || chunkElapsedTime || estimatedTimeRemaining) && (
                          <div className="flex items-center justify-between pt-3 mt-3 border-t border-gray-200 text-xs">
                            {currentChunk && (
                              <div className="flex items-center gap-1.5 text-gray-700">
                                <ArrowPathIcon className="h-3.5 w-3.5 text-blue-500 animate-spin" />
                                <span className="font-medium">
                                  Active: Chunk {currentChunk} of {chunksTotal}
                                </span>
                              </div>
                            )}
                            <div className="flex items-center gap-3">
                              {chunkElapsedTime && (
                                <div className="flex items-center gap-1.5 text-gray-600">
                                  <ClockIcon className="h-3.5 w-3.5 text-gray-500" />
                                  <span>
                                    Elapsed: <span className="text-blue-600 font-medium">{chunkElapsedTime}</span>
                                  </span>
                                </div>
                              )}
                              {estimatedTimeRemaining && (
                                <div className="flex items-center gap-1.5 text-gray-600">
                                  <ClockIcon className="h-3.5 w-3.5 text-gray-500" />
                                  <span>
                                    Remaining: <span className="text-orange-600 font-medium">~{estimatedTimeRemaining}</span>
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  
                  {/* Edit Transcript Button - Show when first chunk is completed */}
                  {data.transcript_chunks_completed >= 1 && data.transcript_status === 'PROCESSING' && (
                    <div className="mt-3 p-4 bg-green-50 rounded-xl border border-green-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <CheckCircleIcon className="h-5 w-5 text-green-600" />
                          <div>
                            <p className="text-sm font-semibold text-green-900">
                              First chunk completed - transcript can be edited
                            </p>
                            <p className="text-xs text-green-700 mt-0.5">
                              You can start editing while the rest processes
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => navigate(`/sessions/${id}/edit`)}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors shadow-sm"
                        >
                          <PencilSquareIcon className="h-5 w-5" />
                          Edit Transcript
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Auto-refresh toggle for PROCESSING status */}
                  {data.transcript_status === 'PROCESSING' && (
                    <div className="flex items-center gap-2 mt-3 p-2 bg-blue-50 rounded-md">
                      <span className="text-xs text-gray-600">Auto-refresh:</span>
                      <button
                        onClick={() => setAutoRefresh(!autoRefresh)}
                        className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                          autoRefresh 
                            ? 'bg-green-500 text-white hover:bg-green-600' 
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        {autoRefresh ? 'ON' : 'OFF'}
                      </button>
                      {autoRefresh && (
                        <span className="text-xs text-gray-500 ml-1">(every 5s)</span>
                      )}
                    </div>
                  )}
                  
                  {/* Report Status Display */}
                  {reportStatus && (
                    <div className="col-span-3 border-t border-gray-200 pt-3 mt-3">
                      <div className="mb-2">
                        <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Report Generation</h4>
                        <ReportStatusDisplay 
                          status={reportStatus.status}
                          startedAt={reportStatus.processing_started_at}
                          completedAt={reportStatus.processing_completed_at}
                          queuedAt={reportStatus.queued_at}
                          duration={reportStatus.processing_duration}
                          errorMessage={reportStatus.error_message}
                          queuePosition={reportStatus.queue_position}
                          estimatedWaitTime={reportStatus.estimated_wait_time}
                          currentStep={reportStatus.current_step}
                          progress={reportStatus.progress}
                          compact={true}
                        />
                        {/* Model and Custom Prompts Info */}
                        {(reportStatus.used_model || reportStatus.used_custom_prompts) && (
                          <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
                            {reportStatus.used_model && (
                              <div className="flex items-center gap-2 text-xs text-gray-600">
                                <span className="font-medium">Model:</span>
                                <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">
                                  {reportStatus.used_model === 'claude-sonnet-4-20250514' ? 'Claude Sonnet 4' : 
                                   reportStatus.used_model === 'gpt-4o' ? 'G  PT-4o' : 
                                   reportStatus.used_model}
                                </span>
                              </div>
                            )}
                            {reportStatus.used_custom_prompts && (
                              <div className="flex items-center gap-2 text-xs text-gray-600">
                                <span className="px-2 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200">
                                  Custom Prompts Used
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Auto-refresh toggle for PROCESSING/PENDING status */}
                      {(reportStatus.status === 'PROCESSING' || reportStatus.status === 'PENDING') && (
                        <div className="flex items-center gap-2 mt-3 p-2 bg-blue-50 rounded-md">
                          <span className="text-xs text-gray-600">Auto-refresh:</span>
                          <button
                            onClick={() => setAutoRefreshReport(!autoRefreshReport)}
                            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                              autoRefreshReport 
                                ? 'bg-green-500 text-white hover:bg-green-600' 
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                          >
                            {autoRefreshReport ? 'ON' : 'OFF'}
                          </button>
                          {autoRefreshReport && (
                            <span className="text-xs text-gray-500 ml-1">(every 3s)</span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Retry button for FAILED status */}
                  {data.transcript_status === 'FAILED' && (
                    <div className="mt-3 space-y-2">
                      <div>
                        <label className="text-xs text-gray-600 mb-1 block">Retry with provider:</label>
                        <select
                          value={selectedProvider}
                          onChange={(e) => setSelectedProvider(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                        >
                          {TRANSCRIPT_PROVIDERS.map((provider) => (
                            <option key={provider.value} value={provider.value}>
                              {provider.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <button
                        onClick={handleStartTranscript}
                        disabled={isGenerating}
                        className="w-full inline-flex items-center justify-center px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {isGenerating ? (
                          <>
                            <ClockIcon className="h-5 w-5 mr-2 animate-spin" />
                            Retrying...
                          </>
                        ) : (
                          <>
                            <PlayIcon className="h-5 w-5 mr-2" />
                            Retry Generation
                          </>
                        )}
                      </button>
                    </div>
                  )}

                  {/* Edit Transcript button for COMPLETED status */}
                  {data.transcript_status === 'COMPLETED' && (
                    <div className="mt-3 space-y-3">
                      {/* Edit Transcript (2/3) + Regenerate (1/3) */}
                      <div className="flex gap-2">
                        <button
                          onClick={() => navigate(`/sessions/${id}/edit`)}
                          className="flex-[2] inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 text-white text-sm font-medium rounded-lg hover:from-blue-700 hover:to-cyan-700 transition-all shadow-md hover:shadow-lg"
                        >
                          <PencilSquareIcon className="h-5 w-5 mr-2" />
                          Edit Transcript
                        </button>
                        <button
                          onClick={handleRegenerateClick}
                          className="flex-[1] inline-flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 text-blue-700 text-sm font-medium rounded-lg hover:bg-blue-100 transition-colors"
                          title="Delete current transcript and regenerate from scratch"
                        >
                          <SparklesIcon className="h-5 w-5" />
                          <span>Regenerate</span>
                        </button>
                      </div>
                      
                      {/* Generate/Regenerate Report (2/3) + Advanced Options (1/3) */}
                      <div className="flex gap-2">
                        <button
                          onClick={handleGenerateReport}
                          disabled={isGeneratingReport || reportStatus?.status === 'PROCESSING' || reportStatus?.status === 'PENDING'}
                          className="flex-[2] inline-flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-sm font-medium rounded-lg hover:from-emerald-700 hover:to-teal-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
                        >
                          <DocumentTextIcon className="h-5 w-5" />
                          {isGeneratingReport ? 'Generating...' : (data.report_url || reportStatus?.status === 'COMPLETED' ? 'Regenerate Report' : 'Generate Report')}
                        </button>
                        <button
                          onClick={() => setShowReportConfigModal(true)}
                          disabled={isGeneratingReport || reportStatus?.status === 'PROCESSING' || reportStatus?.status === 'PENDING'}
                          className="flex-[1] inline-flex items-center justify-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow"
                          title="Configure report generation with custom prompts and AI model selection"
                        >
                          <Cog6ToothIcon className="h-5 w-5" />
                          <span>Advanced</span>
                        </button>
                      </div>
                      
                      {/* Publish Section - Show when report is completed */}
                      {reportStatus?.status === 'COMPLETED' && (
                        <div className="mt-3 space-y-3">
                          {/* Published Link Display */}
                          {data.url && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                              <div className="flex items-center justify-between">
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs font-semibold text-green-900 uppercase mb-1">Published</div>
                                  <a
                                    href={data.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-green-700 hover:text-green-900 hover:underline truncate block"
                                    title={data.url}
                                  >
                                    {data.url}
                                  </a>
                                </div>
                                <a
                                  href={data.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="ml-3 flex-shrink-0 inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-100 border border-green-300 rounded-lg hover:bg-green-200 transition-colors"
                                  title="Open in new tab"
                                >
                                  <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                                  Open
                                </a>
                              </div>
                            </div>
                          )}
                          
                          {/* Publish/Update Button */}
                          <div className="flex gap-2">
                            {data.url ? (
                              <button
                                onClick={handleUpdatePublish}
                                disabled={isPublishing}
                                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
                              >
                                <ArrowPathIcon className="h-5 w-5" />
                                {isPublishing ? 'Updating...' : 'Update Publish'}
                              </button>
                            ) : (
                              <button
                                onClick={handlePublishClick}
                                disabled={isPublishing || !getPublishDestination()}
                                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-sm font-medium rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
                              >
                                <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                                {isPublishing ? 'Publishing...' : `Publish to ${getPublishDestination() === 'faicon' ? 'FAICON' : 'WordPress'}`}
                              </button>
                            )}
                          </div>
                          
                          {/* Publish Destination Info */}
                          {getPublishDestination() && (
                            <div className="text-xs text-gray-500 text-center">
                              Will be published to: <span className="font-medium">{getPublishDestination() === 'faicon' ? 'FAICON' : 'WordPress'}</span>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Action buttons grid */}
                      <div className="grid grid-cols-3 gap-2">

                        {/* View Media */}
                        <button
                          onClick={handleViewMedia}
                          disabled={!fullMediaUrl}
                          className="inline-flex flex-col items-center justify-center px-2 py-3 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title={fullMediaUrl ? "View video or audio in new tab" : "Media not available"}
                        >
                          <PlayIcon className="h-5 w-5 mb-1" />
                          <span>View</span>
                        </button>

                        {/* Download Media */}
                        <button
                          onClick={handleDownloadMedia}
                          disabled={isDownloading}
                          className="inline-flex flex-col items-center justify-center px-2 py-3 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Download video or audio file"
                        >
                          {isDownloading ? (
                            <>
                              <ArrowPathIcon className="h-5 w-5 mb-1 animate-spin" />
                              <span className="text-[10px]">Loading...</span>
                            </>
                          ) : (
                            <>
                              <ArrowDownTrayIcon className="h-5 w-5 mb-1" />
                              <span>Download video/audio</span>
                            </>
                          )}
                        </button>

                        {/* Delete Session */}
                        <button
                          onClick={handleDeleteClick}
                          className="inline-flex flex-col items-center justify-center px-2 py-3 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                          title="Permanently delete this session"
                        >
                          <TrashIcon className="h-5 w-5 mb-1" />
                          <span>Delete</span>
                        </button>
                      </div>
                      
                      {/* Download PDF Report button - only show when report is completed */}
                      {reportStatus?.status === 'COMPLETED' && (
                        <button
                          onClick={handleDownloadPdf}
                          disabled={isDownloadingPdf}
                          className="w-full inline-flex items-center justify-center gap-2 px-3 py-3 text-sm font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Download report as PDF"
                        >
                          {isDownloadingPdf ? (
                            <>
                              <ArrowPathIcon className="h-5 w-5 animate-spin" />
                              <span>Generating PDF...</span>
                            </>
                          ) : (
                            <>
                              <DocumentTextIcon className="h-5 w-5" />
                              <span>Download Report PDF</span>
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Regenerate button for FAILED status (without Edit) */}
                  {data.transcript_status === 'FAILED' && (
                    <div className="mt-3">
                      <button
                        onClick={handleRegenerateClick}
                        className="w-full inline-flex items-center justify-center px-4 py-2 bg-blue-50 text-blue-700 border border-blue-200 text-sm font-medium rounded-lg hover:bg-blue-100 transition-colors"
                      >
                        <SparklesIcon className="h-5 w-5 mr-2" />
                        Clear & Start Fresh
                      </button>
                    </div>
                  )}
                </div>
              )}
              
              <div className="grid grid-cols-3 gap-2">
                <dt className="text-gray-500">Transcript gen</dt>
                <dd className="col-span-2">{transcriptGenSeconds != null ? `${transcriptGenSeconds}s` : '—'}</dd>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <dt className="text-gray-500">Report gen</dt>
                <dd className="col-span-2">{data.report_generation_seconds != null ? `${Math.round(data.report_generation_seconds)}s` : '—'}</dd>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <dt className="text-gray-500">Duration</dt>
                <dd className="col-span-2">{formatDuration(data.duration_seconds)}</dd>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <dt className="text-gray-500">File</dt>
                <dd className="col-span-2 truncate" title={data.original_filename}>{data.original_filename || data.file_path || (data.file_url ? <a className="text-blue-600 hover:underline" href={data.file_url} target="_blank" rel="noreferrer">Open</a> : '—')}</dd>
              </div>
              {data.original_file_id && (
                <div className="grid grid-cols-3 gap-2">
                  <dt className="text-gray-500">File ID</dt>
                  <dd className="col-span-2 text-xs font-mono truncate" title={data.original_file_id}>{data.original_file_id}</dd>
                </div>
              )}
              <div className="grid grid-cols-3 gap-2">
                <dt className="text-gray-500">Processing</dt>
                <dd className="col-span-2 space-y-1">
                  <div>Started: {data.processing_started_at ? new Date(data.processing_started_at).toLocaleString() : '—'}</div>
                  <div>Completed: {data.processing_completed_at ? new Date(data.processing_completed_at).toLocaleString() : '—'}</div>
                </dd>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <dt className="text-gray-500">Created</dt>
                <dd className="col-span-2">{data.created_at ? new Date(data.created_at).toLocaleString() : '—'}</dd>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <dt className="text-gray-500">Updated</dt>
                <dd className="col-span-2">{data.updated_at ? new Date(data.updated_at).toLocaleString() : '—'}</dd>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <dt className="text-gray-500">IDs</dt>
                <dd className="col-span-2 space-x-2">
                  {data.event_id && <Link to={`/events/${data.event_id}`} className="text-blue-600 hover:underline">Event #{data.event_id}</Link>}
                  {data.transcript_id && <Link to={`/sessions/${data.id}/edit`} className="text-blue-600 hover:underline">Transcript #{data.transcript_id}</Link>}
                  {data.report_id && <span>Report #{data.report_id}</span>}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      {/* Regenerate Transcript Confirmation Modal */}
      {showRegenerateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Regenerate Transcript?</h3>
            <div className="mb-6">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-yellow-800 font-medium">
                  ⚠️ Warning: This action cannot be undone
                </p>
              </div>
              <p className="text-gray-700 mb-3">
                This will <strong>permanently delete</strong> the existing transcript and immediately start generating a new one.
              </p>
              
              {/* Provider Selection */}
              <div className="mt-4">
                <label htmlFor="regenerate-provider-select" className="block text-sm font-medium text-gray-700 mb-2">
                  Select Provider for New Transcript
                </label>
                <select
                  id="regenerate-provider-select"
                  value={selectedProvider}
                  onChange={(e) => setSelectedProvider(e.target.value)}
                  disabled={isRegenerating}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                >
                  {TRANSCRIPT_PROVIDERS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {TRANSCRIPT_PROVIDERS.find(p => p.value === selectedProvider)?.description}
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowRegenerateModal(false)}
                disabled={isRegenerating}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmRegenerate}
                disabled={isRegenerating}
                className="flex-1 px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center justify-center gap-2"
              >
                {isRegenerating ? (
                  <>
                    <ArrowPathIcon className="h-5 w-5 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <SparklesIcon className="h-5 w-5" />
                    Delete & Generate
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Session Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Delete Session?</h3>
            <div className="mb-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-800 font-medium">
                  🗑️ Warning: This action is permanent and cannot be undone
                </p>
              </div>
              <p className="text-gray-700 mb-3">
                This will <strong>permanently delete</strong> the entire session including:
              </p>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-1 mb-3">
                <li>All transcripts and their versions</li>
                <li>Generated reports</li>
                <li>All associated metadata</li>
              </ul>
              <p className="text-gray-700 font-semibold">
                Session: <span className="text-gray-900">{data?.title || `Session ${id}`}</span>
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center justify-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <ArrowPathIcon className="h-5 w-5 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <TrashIcon className="h-5 w-5" />
                    Delete Session
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {notif && (
        <Notification
          type={notif.type}
          title={notif.title}
          message={notif.message}
          onClose={() => setNotif(null)}
          duration={5000}
        />
      )}

      {/* Report Configuration Modal */}
      <ReportConfigModal
        isOpen={showReportConfigModal}
        onClose={() => setShowReportConfigModal(false)}
        onSubmit={handleReportConfigSubmit}
      />
      <PublishModal
        isOpen={showPublishModal}
        onClose={() => setShowPublishModal(false)}
        onConfirm={handleConfirmPublish}
        destination={publishDestination}
        isUpdate={!!data?.url}
        isPublishing={isPublishing}
      />
    </div>
  );
}


